// src/pages/Home.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.tsx';
import { Clipboard, Users, FolderPlus, LogOut, ChevronDown, User } from 'lucide-react';
import { Permissions } from '../constants/permissions.ts';

// User Menu Component
interface UserMenuProps {
  user: any;
  onLogout: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white">
          {user?.name?.charAt(0) || <User size={16} />}
        </div>
        <ChevronDown size={16} className={`text-gray-600 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 ring-1 ring-black ring-opacity-5">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.role}</p>
          </div>
          <button
            onClick={onLogout}
            className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <div className="flex items-center">
              <LogOut size={16} className="mr-2" />
              {t('navigation.logout')}
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

const Home: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Navigation cards configuration
  const navigationCards = [
    {
      title: t('navigation.dashboard'),
      description: t('dashboard.cardDescriptions.projectsDashboard'),
      icon: <Clipboard className="w-10 h-10 sm:w-12 sm:h-12 text-indigo-500" />,
      path: '/projects',
      color: 'bg-gradient-to-br from-indigo-50 to-blue-100',
      show: true
    },
    {
      title: t('navigation.createProject'),
      description: t('dashboard.cardDescriptions.createProject'),
      icon: <FolderPlus className="w-10 h-10 sm:w-12 sm:h-12 text-green-500" />,
      path: '/create',
      color: 'bg-gradient-to-br from-green-50 to-emerald-100',
      show: hasPermission(Permissions.CREATE_PROJECT)
    },
    {
      title: t('navigation.userManagement'),
      description: t('dashboard.cardDescriptions.userManagement'),
      icon: <Users className="w-10 h-10 sm:w-12 sm:h-12 text-purple-500" />,
      path: '/users',
      color: 'bg-gradient-to-br from-purple-50 to-violet-100',
      show: hasPermission(Permissions.MANAGE_USERS)
    }
  ].filter(card => card.show);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with user info */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Project Management</h1>
              <p className="mt-1 text-sm text-gray-500">Welcome back</p>
            </div>
            <UserMenu user={user} onLogout={handleLogout} />
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {navigationCards.map((card, index) => (
            <div
              key={index}
              onClick={() => navigate(card.path)}
              className={`${card.color} rounded-xl shadow-md p-4 sm:p-6 lg:p-8 transition-all duration-200 transform hover:scale-105 hover:shadow-lg cursor-pointer`}
            >
              <div className="flex flex-col h-full">
                <div className="mb-4 sm:mb-6">
                  <div className="p-2 sm:p-3 bg-white bg-opacity-50 rounded-full inline-block">
                    {card.icon}
                  </div>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">{card.title}</h2>
                <p className="text-sm sm:text-base text-gray-700 flex-grow leading-relaxed">{card.description}</p>
                <div className="mt-4 sm:mt-6 flex justify-end">
                  <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-white bg-opacity-50 rounded-lg text-xs sm:text-sm font-medium text-indigo-600 hover:bg-opacity-70">
                    Go to {card.title} &rarr;
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Home;